import React, { useState, useEffect } from "react";
import { permissionService } from "../services/permissionService";
import { userService } from "../services/userService";

const RolesManagement = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({ role_name: "" });

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    setLoading(true);
    try {
      const response = await userService.getRoles();
      setRoles(response.data.data);
    } catch (error) {
      console.error("Error loading roles:", error);
      alert(
        "Error loading roles: " +
          (error.response?.data?.message || error.message)
      );
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.role_name.trim()) {
      alert("Role name is required!");
      return;
    }
    try {
      setSaving(true);
      if (editingRole) {
        await permissionService.updateRole(editingRole.id, {
          role_name: formData.role_name,
        });
        alert("Role updated successfully!");
      } else {
        await permissionService.createRole({ role_name: formData.role_name });
        alert("Role created successfully!");
      }
      setFormData({ role_name: "" });
      setShowForm(false);
      setEditingRole(null);
      loadRoles();
    } catch (error) {
      console.error("Error saving role:", error);
      alert(error.response?.data?.message || "Error saving role");
    }
    setSaving(false);
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    setFormData({ role_name: role.role_name });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this role?")) {
      return;
    }
    try {
      setLoading(true);
      // Note: You may need to add deleteRole endpoint in backend
      alert("Delete functionality needs to be implemented in backend");
      loadRoles();
    } catch (error) {
      alert("Error: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8">
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Roles Management</h2>
          <button
            onClick={() => {
              setShowForm(true);
              setEditingRole(null);
              setFormData({ role_name: "" });
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            + Create Role
          </button>
        </div>

        {showForm && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6 rounded-lg shadow-lg">
              <h3 className="text-xl font-bold mb-4">
                {editingRole ? "Edit Role" : "Create New Role"}
              </h3>
              <form onSubmit={handleSubmit} className="flex gap-4 items-end">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Role Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.role_name}
                    onChange={(e) => setFormData({ role_name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter role name (e.g., Manager, Editor)"
                  />
                </div>
                <button
                  type="submit"
                  disabled={saving}
                  className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? "Saving..." : editingRole ? "Update" : "Create"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingRole(null);
                    setFormData({ role_name: "" });
                  }}
                  className="bg-gray-500 text-white px-6 py-2 rounded-md hover:bg-gray-600"
                >
                  Cancel
                </button>
              </form>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-8">Loading...</div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-teal-600 to-emerald-600 ">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                    Sr. No.
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                    ID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                    Role Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                    Created At
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {roles.map((role, index) => (
                  <tr key={role.id}>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {index + 1}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {role.id}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {role.role_name}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {role.created_at
                        ? new Date(role.created_at).toLocaleDateString("en-GB")
                        : "-"}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleEdit(role)}
                        className="text-blue-600 hover:text-blue-900 p-2 rounded hover:bg-blue-50 transition-colors"
                        title="Edit Role"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default RolesManagement;
